import { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  api,
  type PromotionProposal,
  type PromotionStatus,
  type PromotionType,
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { cn } from '@/lib/utils';
import {
  Rocket,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  Shield,
  RefreshCw,
} from 'lucide-react';

/* ─── Constants ────────────────────────────────────────────────────────────── */

const STATUS_FILTERS: Array<{ value: '' | PromotionStatus; label: string }> = [
  { value: '', label: 'Todos os status' },
  { value: 'pending', label: 'Pendente' },
  { value: 'approved', label: 'Aprovado' },
  { value: 'rejected', label: 'Rejeitado' },
  { value: 'applied', label: 'Aplicado' },
  { value: 'cancelled', label: 'Cancelado' },
];

const TYPE_FILTERS: Array<{ value: '' | PromotionType; label: string }> = [
  { value: '', label: 'Todos os tipos' },
  { value: 'skill', label: 'Skill' },
  { value: 'agent', label: 'Agent' },
];

const STATUS_BADGE: Record<PromotionStatus, 'default' | 'info' | 'success' | 'warning' | 'danger' | 'brand'> = {
  pending: 'warning',
  approved: 'info',
  rejected: 'danger',
  applied: 'success',
  cancelled: 'default',
};

const MODE_LABEL: Record<string, string> = {
  cron_candidate: 'Cron (auto)',
  manual_force: 'Manual force',
};

/* ─── Page ─────────────────────────────────────────────────────────────────── */

export function AdminPromotionsPage() {
  const { tenant } = useAuth();
  const isAdmin = !!tenant?.is_admin;

  const [promotions, setPromotions] = useState<PromotionProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'' | PromotionStatus>('pending');
  const [typeFilter, setTypeFilter] = useState<'' | PromotionType>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<PromotionProposal | null>(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [actionInFlight, setActionInFlight] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.listAdminPromotions({
        status: statusFilter,
        type: typeFilter,
      });
      setPromotions(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar propostas');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => {
    if (isAdmin) void load();
  }, [load, isAdmin]);

  async function handleApprove(proposal: PromotionProposal) {
    setActionInFlight(proposal.id);
    try {
      const updated = await api.approvePromotion(proposal.id);
      setPromotions((prev) => prev.map((p) => (p.id === proposal.id ? updated : p)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao aprovar');
    } finally {
      setActionInFlight(null);
    }
  }

  async function handleReject() {
    if (!rejectTarget || !rejectNotes.trim()) return;
    setActionInFlight(rejectTarget.id);
    try {
      const updated = await api.rejectPromotion(rejectTarget.id, rejectNotes.trim());
      setPromotions((prev) => prev.map((p) => (p.id === rejectTarget.id ? updated : p)));
      setRejectTarget(null);
      setRejectNotes('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao rejeitar');
    } finally {
      setActionInFlight(null);
    }
  }

  if (!isAdmin) {
    return (
      <div>
        <PageHeader
          title="Promoções"
          description="Revisão de propostas de promoção de skills e agents"
          className="mb-6"
        />
        <Card className="p-8 text-center">
          <Shield className="w-10 h-10 mx-auto text-raios-muted mb-3" />
          <p className="text-sm font-semibold text-raios-text mb-1">Acesso restrito</p>
          <p className="text-xs text-raios-muted">
            Esta área requer a role <span className="font-mono">raios_admin</span>.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Promoções"
        description="Revise e aprove propostas de promoção de skills e agents"
        actions={
          <Button variant="default" size="sm" onClick={load} loading={loading}>
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </Button>
        }
        className="mb-6"
      />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <Select
          options={STATUS_FILTERS.map((f) => ({ value: f.value, label: f.label }))}
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as '' | PromotionStatus)}
          className="sm:w-56"
        />
        <Select
          options={TYPE_FILTERS.map((f) => ({ value: f.value, label: f.label }))}
          value={typeFilter}
          onChange={(v) => setTypeFilter(v as '' | PromotionType)}
          className="sm:w-56"
        />
      </div>

      {error && (
        <Card className="bg-raios-danger/10 px-4 py-3 mb-4">
          <p className="text-raios-danger-text text-sm">{error}</p>
        </Card>
      )}

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      ) : promotions.length === 0 ? (
        <EmptyState
          icon={Rocket}
          title="Nenhuma proposta encontrada"
          description="Não há propostas de promoção correspondendo aos filtros atuais."
        />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-raios-elevated/40 text-left">
                <tr className="border-b border-raios-border">
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-raios-muted w-8"></th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-raios-muted">Tipo</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-raios-muted">Slug</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-raios-muted">Escopo</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-raios-muted">Modo</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-raios-muted">Proposto por</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-raios-muted">Proposto em</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-raios-muted">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-raios-muted text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {promotions.map((p) => (
                  <PromotionRow
                    key={p.id}
                    proposal={p}
                    expanded={expandedId === p.id}
                    onToggle={() => setExpandedId(expandedId === p.id ? null : p.id)}
                    onApprove={() => handleApprove(p)}
                    onReject={() => {
                      setRejectTarget(p);
                      setRejectNotes('');
                    }}
                    actionLoading={actionInFlight === p.id}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Reject modal */}
      <Dialog
        open={!!rejectTarget}
        onOpenChange={(next) => {
          if (!next) {
            setRejectTarget(null);
            setRejectNotes('');
          }
        }}
      >
        <DialogContent size="default" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Rejeitar Promoção</DialogTitle>
            <DialogDescription>
              Informe o motivo da rejeição. Esta nota ficará visível ao proponente.
            </DialogDescription>
          </DialogHeader>

          {rejectTarget && (
            <div className="mt-3 text-xs text-raios-muted">
              <span className="font-mono text-raios-text">{rejectTarget.slug}</span>
              {' · '}
              {rejectTarget.source_scope} → {rejectTarget.target_scope}
            </div>
          )}

          <div className="mt-4">
            <Textarea
              label="Motivo (obrigatório)"
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              placeholder="Descreva o motivo da rejeição..."
              rows={5}
            />
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setRejectTarget(null);
                setRejectNotes('');
              }}
              disabled={actionInFlight === rejectTarget?.id}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              loading={actionInFlight === rejectTarget?.id}
              disabled={!rejectNotes.trim() || actionInFlight === rejectTarget?.id}
            >
              <X className="w-4 h-4" />
              Confirmar Rejeição
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ─── Row ──────────────────────────────────────────────────────────────────── */

interface PromotionRowProps {
  proposal: PromotionProposal;
  expanded: boolean;
  onToggle: () => void;
  onApprove: () => void;
  onReject: () => void;
  actionLoading: boolean;
}

function PromotionRow({ proposal, expanded, onToggle, onApprove, onReject, actionLoading }: PromotionRowProps) {
  const isPending = proposal.status === 'pending';
  const linkTo =
    proposal.type === 'skill'
      ? `/skills/${proposal.slug}`
      : `/agents/${proposal.slug}`;

  const diffPreview = useMemo(() => {
    if (!proposal.diff_snapshot) return null;
    try {
      return JSON.stringify(proposal.diff_snapshot, null, 2);
    } catch {
      return String(proposal.diff_snapshot);
    }
  }, [proposal.diff_snapshot]);

  return (
    <>
      <tr className="border-b border-raios-border/60 hover:bg-raios-elevated/20">
        <td className="px-4 py-3">
          <button
            type="button"
            onClick={onToggle}
            aria-label={expanded ? 'Recolher detalhes' : 'Expandir detalhes'}
            className="text-raios-muted hover:text-raios-text transition-colors"
          >
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </td>
        <td className="px-4 py-3">
          <Badge variant={proposal.type === 'skill' ? 'brand' : 'info'}>
            {proposal.type}
          </Badge>
        </td>
        <td className="px-4 py-3">
          <Link
            to={linkTo}
            className="text-raios-accent-text hover:underline font-mono text-xs"
          >
            {proposal.slug}
          </Link>
        </td>
        <td className="px-4 py-3 text-xs text-raios-text-secondary">
          <span className="font-mono">{proposal.source_scope}</span>
          <span className="mx-1.5 text-raios-muted">→</span>
          <span className="font-mono">{proposal.target_scope}</span>
        </td>
        <td className="px-4 py-3 text-xs text-raios-text-secondary">
          {MODE_LABEL[proposal.mode] ?? proposal.mode}
        </td>
        <td className="px-4 py-3 text-xs text-raios-text-secondary">
          {proposal.proposed_by_email ?? proposal.proposed_by ?? '—'}
        </td>
        <td className="px-4 py-3 text-xs text-raios-muted">
          {new Date(proposal.proposed_at).toLocaleString('pt-BR')}
        </td>
        <td className="px-4 py-3">
          <Badge variant={STATUS_BADGE[proposal.status] ?? 'default'}>
            {proposal.status}
          </Badge>
        </td>
        <td className="px-4 py-3 text-right">
          {isPending ? (
            <div className="flex items-center gap-2 justify-end">
              <Button
                variant="brand"
                size="sm"
                onClick={onApprove}
                loading={actionLoading}
                disabled={actionLoading}
              >
                <Check className="w-3.5 h-3.5" />
                Aprovar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onReject}
                disabled={actionLoading}
              >
                <X className="w-3.5 h-3.5" />
                Rejeitar
              </Button>
            </div>
          ) : (
            <span className="text-xs text-raios-muted">—</span>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-raios-elevated/20">
          <td colSpan={9} className="px-4 py-4">
            <div className="space-y-3">
              {proposal.review_notes && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-raios-muted mb-1">
                    Review notes
                  </p>
                  <p className="text-xs text-raios-text-secondary whitespace-pre-wrap">
                    {proposal.review_notes}
                  </p>
                </div>
              )}
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-widest text-raios-muted mb-1">
                  Diff snapshot
                </p>
                {diffPreview ? (
                  <pre
                    className={cn(
                      'text-[11px] font-mono text-raios-text-secondary',
                      'bg-raios-bg border border-raios-border rounded-md p-3 max-h-80 overflow-auto'
                    )}
                  >
                    {diffPreview}
                  </pre>
                ) : (
                  <p className="text-xs text-raios-muted">Sem diff disponível.</p>
                )}
              </div>
              {proposal.reviewed_at && (
                <div className="text-xs text-raios-muted">
                  Revisado em {new Date(proposal.reviewed_at).toLocaleString('pt-BR')}
                  {proposal.reviewed_by && ` por ${proposal.reviewed_by}`}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
